import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, Search, Trash2, LayoutGrid, Rows3 } from 'lucide-react';
import api, { apiError } from '../lib/api';
import { formatIDR } from '../lib/format';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { HapusCari } from '../components/HapusCari';
import { TombolWa } from '../components/KirimWa';

const EMPTY = { name: '', whatsapp: '', address: '', notes: '' };

export default function Customers() {
  const [customers, setCustomers] = useState(null);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState('list');
  const navigate = useNavigate();

  const load = () => api.get('/customers').then((r) => setCustomers(r.data));
  useEffect(() => {
    load();
  }, []);

  const filtered = (customers || []).filter((c) => c.name.toLowerCase().includes(q.toLowerCase()));

  const submit = async () => {
    if (!form.name.trim()) return toast.error('Nama wajib diisi');
    setSaving(true);
    try {
      await api.post('/customers', form);
      toast.success('Customer ditambahkan');
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (e, c) => {
    e.stopPropagation();
    try {
      await api.delete(`/customers/${c.id}`);
      toast.success(`Customer ${c.name} dihapus`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="space-y-5" data-testid="customers-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-3xl text-ink tracking-tight">Customer</h1>
        <div className="flex items-center gap-2">
          <div className="flex gap-1.5 bg-white border border-border rounded-xl p-1" data-testid="customer-view-toggle">
            <button data-testid="customer-view-list" onClick={() => setView('list')}
              className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${view === 'list' ? 'bg-brown text-white' : 'text-mocha hover:bg-ivory'}`}>
              <Rows3 size={14} /> List
            </button>
            <button data-testid="customer-view-grid" onClick={() => setView('grid')}
              className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${view === 'grid' ? 'bg-brown text-white' : 'text-mocha hover:bg-ivory'}`}>
              <LayoutGrid size={14} /> Kotak
            </button>
          </div>
          <button
            data-testid="add-customer-button"
            onClick={() => setOpen(true)}
            className="inline-flex items-center gap-2 rounded-full bg-emerald text-white px-5 py-2.5 text-sm font-semibold hover:bg-emerald-light transition-colors"
          >
            <Plus size={16} /> Tambah Customer
          </button>
        </div>
      </div>

      <div className="relative max-w-sm">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          data-testid="customer-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Cari nama customer…"
          className="w-full rounded-full border border-gold/20 bg-white pl-10 pr-9 py-2.5 text-sm outline-none focus:ring-2 focus:ring-emerald/40"
        />
        <HapusCari show={!!q} onClear={() => setQ('')} testid="customer-search-clear" />
      </div>

      {!customers ? (
        <p className="text-sm text-muted-foreground py-10 text-center">Memuat customer…</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground py-10 text-center" data-testid="customers-empty">Belum ada customer.</p>
      ) : view === 'list' ? (
        <div className="space-y-2" data-testid="customers-list">
          {filtered.map((c) => (
            <div
              key={c.id}
              data-testid={`customer-row-${c.id}`}
              onClick={() => navigate(`/customer/${c.id}`)}
              className="cursor-pointer bg-white rounded-xl border border-gold/20 shadow-soft px-4 py-3 flex items-center gap-3 hover:bg-ivory/60 transition-colors"
            >
              <div className="w-9 h-9 rounded-full bg-emerald/10 text-emerald flex items-center justify-center font-display font-bold shrink-0">
                {c.name.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-ink text-sm truncate">{c.name}</p>
                <p className="text-xs text-muted-foreground">{c.whatsapp || '-'}</p>
              </div>
              <span className="text-xs text-mocha whitespace-nowrap hidden sm:block">{c.transaction_count} transaksi</span>
              <span className="text-xs font-bold text-emerald whitespace-nowrap">{formatIDR(c.total_spent)}</span>
              {c.whatsapp && (
                <TombolWa
                  target={{ kind: 'customer', name: c.name, whatsapp: c.whatsapp }}
                  testid={`customer-wa-${c.id}`}
                  title="Kirim WhatsApp"
                  className="p-2 rounded-full bg-[#25D366]/10 text-[#1da851] shrink-0"
                />
              )}
              <button
                data-testid={`customer-delete-${c.id}`}
                onClick={(e) => remove(e, c)}
                className="p-2 rounded-full bg-danger/10 text-danger shrink-0"
              >
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
          {filtered.map((c) => (
            <div
              key={c.id}
              data-testid={`customer-card-${c.id}`}
              onClick={() => navigate(`/customer/${c.id}`)}
              className="cursor-pointer bg-white rounded-2xl border border-gold/20 shadow-soft p-5 card-lift"
            >
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-emerald/10 text-emerald flex items-center justify-center font-display font-bold text-lg">
                  {c.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-ink truncate">{c.name}</p>
                  <p className="text-xs text-muted-foreground">{c.whatsapp || '-'}</p>
                </div>
                {c.whatsapp && (
                  <TombolWa
                    target={{ kind: 'customer', name: c.name, whatsapp: c.whatsapp }}
                    testid={`customer-wa-${c.id}`}
                    title="Kirim WhatsApp"
                    className="p-2 rounded-full bg-[#25D366]/10 text-[#1da851]"
                  />
                )}
                <button
                  data-testid={`customer-delete-${c.id}`}
                  onClick={(e) => remove(e, c)}
                  className="p-2 rounded-full bg-danger/10 text-danger"
                >
                  <Trash2 size={15} />
                </button>
              </div>
              <div className="flex gap-4 mt-4 text-center">
                <div className="flex-1 rounded-xl bg-ivory py-2">
                  <p className="font-display text-lg text-emerald">{c.transaction_count}</p>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Transaksi</p>
                </div>
                <div className="flex-1 rounded-xl bg-ivory py-2">
                  <p className="font-display text-lg text-emerald">{formatIDR(c.total_spent)}</p>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Total Belanja</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="customer-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-emerald text-xl">Tambah Customer</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {[
              ['customer-name-input', 'Nama', 'name', 'text'],
              ['customer-whatsapp-input', 'WhatsApp', 'whatsapp', 'text'],
              ['customer-address-input', 'Alamat', 'address', 'text'],
            ].map(([testid, label, key]) => (
              <div key={key}>
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</label>
                <input
                  data-testid={testid}
                  {...(key === 'whatsapp' ? { type: 'tel', inputMode: 'numeric', autoComplete: 'tel' } : {})}
                  value={form[key]}
                  onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                  className="mt-1.5 w-full rounded-xl border border-input bg-ivory/50 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-emerald/40"
                />
              </div>
            ))}
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Catatan</label>
              <textarea
                data-testid="customer-notes-input"
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                rows={2}
                className="mt-1.5 w-full rounded-xl border border-input bg-ivory/50 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-emerald/40"
              />
            </div>
            <button
              data-testid="save-customer-button"
              onClick={submit}
              disabled={saving}
              className="w-full rounded-full bg-emerald text-white py-3 font-semibold text-sm hover:bg-emerald-light transition-colors disabled:opacity-60"
            >
              {saving ? 'Menyimpan…' : 'Simpan Customer'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
